import { motion } from "motion/react";

import { cn } from "@/lib/utils";

export interface TrendPoint {
  key: string;
  /** Ustun ostidagi qisqa yorliq. */
  label: string;
  value: number;
  /** Ikkinchi (fon) qiymat — masalan, rejadagi vazifalar. */
  base?: number;
}

/**
 * Kutubxonasiz ustunli grafik. Fon ustuni (`base`) berilsa — reja va
 * bajarilish ustma-ust: to'liq ustun reja, rangli qismi bajarilgani.
 * Ko'p ustunda yorliqlar siyraklashadi, qiymat esa hover'da chiqadi.
 */
export function TrendChart({
  points,
  className,
  tone = "bg-primary",
  label,
}: {
  points: TrendPoint[];
  className?: string;
  /** Asosiy ustun rangi (token klassi). */
  tone?: string;
  /** Skrinrider uchun grafik nomi. */
  label: string;
}) {
  const withBase = points.some(point => point.base !== undefined);
  const max = Math.max(1, ...points.map(point => Math.max(point.value, point.base ?? 0)));
  const step = points.length > 16 ? Math.ceil(points.length / 8) : 1;

  return (
    <div
      className={cn("flex h-52 items-end gap-[3px] sm:gap-1", className)}
      role="img"
      aria-label={label}
    >
      {points.map((point, index) => {
        const height = (value: number) => (value <= 0 ? 0 : Math.max(3, (value / max) * 100));
        const valueHeight = height(point.value);
        return (
          <div
            key={point.key}
            className="group/bar relative flex h-full min-w-0 flex-1 flex-col items-center gap-1.5"
            title={
              withBase
                ? `${point.label}: ${point.value} / ${point.base ?? 0}`
                : `${point.label}: ${point.value}`
            }
          >
            <div className="relative flex w-full flex-1 items-end">
              <span className="bg-foreground text-background pointer-events-none absolute -top-1 left-1/2 z-10 -translate-x-1/2 translate-y-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap opacity-0 shadow-md transition-all group-hover/bar:-translate-y-1 group-hover/bar:opacity-100">
                {withBase ? `${point.value}/${point.base ?? 0}` : point.value}
              </span>
              <div className="bg-muted/70 absolute inset-0 rounded-md" />
              {withBase ? (
                <motion.div
                  className="bg-primary/15 absolute bottom-0 w-full rounded-md"
                  initial={{ height: 0 }}
                  animate={{ height: `${height(point.base ?? 0)}%` }}
                  transition={{ duration: 0.6, delay: index * 0.015, ease: [0.16, 1, 0.3, 1] }}
                />
              ) : null}
              <motion.div
                className={cn(
                  "relative w-full rounded-md opacity-80 transition-opacity group-hover/bar:opacity-100",
                  tone,
                )}
                initial={{ height: 0 }}
                animate={{ height: `${valueHeight}%` }}
                transition={{ duration: 0.7, delay: 0.1 + index * 0.015, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
            <span
              className={cn(
                "text-muted-foreground h-4 text-[10px] whitespace-nowrap",
                index % step !== 0 && "invisible",
              )}
            >
              {point.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
